import React from 'react';
import PresignedImage from '../../../components/common/PresignedImage';
import PresignedLink from '../../../components/common/PresignedLink';
import { splitEventAttachments } from '../utils/eventTimelineUtils';

const formatSize = (bytes) => {
  const n = Number(bytes);
  if (!n || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Renders event images (inline) and non-image files (downloadable list).
 */
const EventAttachmentsDisplay = ({
  attachments = [],
  imagesOnly = false,
  compact = false,
  className = '',
}) => {
  const { images, files } = splitEventAttachments(attachments);
  const showFiles = !imagesOnly && files.length > 0;
  if (!images.length && !showFiles) return null;

  return (
    <div className={`space-y-3 ${className}`}>
      {images.length > 0 && (
        <div className={`grid gap-2 ${compact ? 'grid-cols-2' : 'grid-cols-2 sm:grid-cols-3'}`}>
          {images.map((img, idx) => (
            <div
              key={img.id || img.fileUrl || `img-${idx}`}
              className={`overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 ${compact ? 'aspect-video' : 'aspect-square'}`}
            >
              <PresignedImage
                fileKey={img.fileUrl}
                alt={img.fileName || 'Event image'}
                originalName={img.fileName}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      )}

      {showFiles && (
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-3 py-2 bg-gray-50 dark:bg-gray-800/80 border-b border-gray-200 dark:border-gray-700">
            <p className="text-[10px] font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
              Attachments
            </p>
          </div>
          <ul className="divide-y divide-gray-100 dark:divide-gray-800">
            {files.map((file, idx) => (
              <li
                key={file.id || file.fileUrl || `file-${idx}`}
                className="flex items-center gap-3 px-3 py-2.5"
              >
                <span className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-50 dark:bg-primary-950/40 text-primary-600 dark:text-primary-300 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                  </svg>
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                    {file.fileName || 'Attachment'}
                  </p>
                  {file.fileSize ? (
                    <p className="text-[11px] text-gray-500 dark:text-gray-400">{formatSize(file.fileSize)}</p>
                  ) : null}
                </div>
                <PresignedLink
                  fileKey={file.fileUrl}
                  download
                  originalName={file.fileName}
                  className="flex-shrink-0 text-xs font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download
                </PresignedLink>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default EventAttachmentsDisplay;
